/* import __COLOCATED_TEMPLATE__ from './topic-trends.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from 'tracked-built-ins';
import type Range from 'embercom/models/reporting/range';
import { AsyncData } from 'embercom/resources/utils/async-data';
import { use } from 'ember-resources/util/function-resource';
import { fetchTopicsTaxonomy } from 'embercom/components/reporting/automation/ai-insights/fin-insights/topics-data-resources';
import type {
  Topic,
  TopicTaxonomy,
} from 'embercom/components/reporting/automation/ai-insights/fin-insights/topics-data-resources';
import { inject as service } from '@ember/service';
import MetricPickerItem from './topic-trends/metric-picker-item';
import { metricConfigs } from './topic-trends/common';
import type IntlService from 'ember-intl/services/intl';
import { insightsMetrics } from 'embercom/components/reporting/automation/ai-insights/fin-insights/jobs';
import { type MetricTableJob } from '../../ai-insights/fin-insights/metrics-table';
import { and } from '../../ai-insights/fin-insights/sankey-config';
interface Args {}

const ALL_TOPIC_LIMIT = Number.POSITIVE_INFINITY;

export default class TopicTrends extends Component<Args> {
  @service declare appService: any;
  @service declare intercomEventService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked topicLimit = 10;
  @tracked selectedMetricId = Object.keys(metricConfigs)[0];
  @tracked selectedTopicId: string | null = null;

  @tracked exploreConversationsState: {
    selectedTopic: Topic;
    selectedSubTopic: string | null;
    dateRange: Range;
    conversationCount: number;
  } | null = null;

  @use topicsTaxonomy = AsyncData<TopicTaxonomy>(({ abortSignal }) =>
    fetchTopicsTaxonomy(this.appService.app.id, abortSignal),
  );

  constructor(owner: any, args: Args) {
    super(owner, args);
    this.topicsTaxonomy.isLoading; // this is a hack to eagerly load the topics taxonomy
  }

  get showingAllTopics() {
    return this.topicLimit === ALL_TOPIC_LIMIT;
  }

  get topicLimitOptions() {
    let labelForCount = (count: number) =>
      this.intl.t(
        'components.reporting.automation.fin-insights.analyse.topic-trends.top-topics-by-volume',
        { count },
      );
    return [
      { text: labelForCount(5), value: 5 },
      { text: labelForCount(10), value: 10 },
      { text: labelForCount(15), value: 15 },
      { text: labelForCount(20), value: 20 },
      { text: labelForCount(25), value: 25 },
      {
        text: this.intl.t(
          'components.reporting.automation.fin-insights.analyse.topic-trends.top-topics-all',
        ),
        value: ALL_TOPIC_LIMIT,
      },
    ];
  }

  get metricOptions() {
    return Object.entries(metricConfigs).map(([metricId, metricConfig]) => ({
      text: this.intl.t(metricConfig.label),
      value: metricId,
      component: MetricPickerItem,
      componentAttrs: { color: metricConfig.metricPickerItemClass },
    }));
  }

  get selectedTopic() {
    if (!this.selectedTopicId || this.topicsTaxonomy.isLoading) {
      return null;
    }

    return (
      this.topicsTaxonomy.value![this.selectedTopicId] ?? {
        name: this.selectedTopicId,
      }
    );
  }

  get metricsTableJob(): MetricTableJob {
    let highlightedMetricIndex =
      Object.keys(metricConfigs).findIndex((metricId) => metricId === this.selectedMetricId) + 1;
    let metrics = insightsMetrics(this.intl);
    return {
      id: 'metrics-table',
      showSuggestions: false,
      table: {
        cta: 'components.reporting.automation.fin-insights.analyse.topic-trends.table-cta',
        filters: and(...[]),
        chartSeries: [
          metrics.volume,
          metrics.aiCsatScore,
          metrics.involvementRate,
          metrics.resolutionRate,
          metrics.handlingTimeMedian,
          metrics.firstResponseTime,
        ],
        highlightedMetricIndex,
      },
    };
  }

  @action
  setSelectedTopicNull() {
    this.selectedTopicId = null;
  }

  get title() {
    if (this.selectedTopic) {
      return this.intl.t(
        'components.reporting.automation.fin-insights.analyse.topic-trends.treemap.subtopics-count',
        { count: this.topicLimit },
      );
    }
    return this.intl.t(
      'components.reporting.automation.fin-insights.analyse.topic-trends.treemap.topics-count',
      { count: this.topicLimit },
    );
  }

  @action
  async onShowConversationExplorer(
    topic: string,
    subtopic: string | null,
    dateRange: Range,
    conversationCount: number,
  ) {
    if (this.topicsTaxonomy.isLoading) {
      await this.topicsTaxonomy.promise;
    }
    let topicObject = this.topicsTaxonomy.value?.[topic] ?? {
      name: topic,
      description: null,
      subtopics: {},
    };
    this.exploreConversationsState = {
      selectedTopic: topicObject,
      selectedSubTopic: subtopic,
      dateRange,
      conversationCount,
    };
  }

  @action
  async onShowTableConversationExplorer(
    dateRange: Range,
    topic: string,
    subtopic: string | null,
    _type: 'suggestions' | 'conversations',
    conversationCount: number,
  ) {
    this.onShowConversationExplorer(topic, subtopic, dateRange, conversationCount);
  }

  @action
  closeConversationExplorer() {
    this.exploreConversationsState = null;
  }

  @action
  trackAnalyticsEvent(action: string, object: string, extraContext: Record<string, any> = {}) {
    this.intercomEventService.trackAnalyticsEvent({
      place: 'analyze/topic-trends',
      action,
      object,
      ...extraContext,
    });
  }

  get serializedReport() {
    return {
      id: 'topic-trends',
      date_range: {
        selection: 'past_4_weeks',
      },
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TopicTrends': typeof TopicTrends;
  }
}
